// ============================================================================
// Expediente documental del embarque — cliente tipado del API `/documents/*`
// (CC-CONSOLE #1632, épica #1637; API de #1630).
// ============================================================================
// Todo lo que la consola sabe del expediente pasa por aquí: tablero, sujetos,
// checklist, subida con progreso, aprobación, repositorio, plantillas y
// vencimientos. Los llamados de generación y lectura con IA (#1631) van en la
// misma rama del contrato (`/documents/generators`, `…/generate`, `…/extract`,
// `…/confirm-extracted`): si el backend aún no los tiene, responden 404 y la UI
// muestra «próximamente» sin romper (`isNotAvailable`).
// ============================================================================
import axios, { type AxiosProgressEvent } from "axios";
import api, { apiFormData } from "../utils/api";
import {
  DOCUMENT_STATUSES,
  DOCUMENT_TYPES,
  INCOTERMS,
  SUBJECT_STATES,
  type SubjectState,
  type DocumentCategory,
  type DocumentDirection,
  type DocumentMode,
  type DocumentProvider,
  type DocumentSource,
  type DocumentStatus,
  type DocumentTypeSpec,
  type SubjectKind,
} from "../utils/documentTypes";

// ---------------------------------------------------------------------------
// Tipos (espejo de shipmentDocumentsService.decorate / README del módulo)
// ---------------------------------------------------------------------------

export interface DocumentSubjectRef {
  kind: SubjectKind;
  id: string;
}

export interface DocumentVersion {
  n: number;
  storage_path?: string;
  file_name: string | null;
  mime: string | null;
  size: number | null;
  uploaded_by: string | null;
  uploaded_at: string | null;
  source: DocumentSource | null;
}

export interface DocumentReview {
  by: string | null;
  at: string | null;
  note: string | null;
  status: DocumentStatus;
}

export type DocumentHistoryAction = "materialized" | "version" | "reference" | "status" | "note" | "generated" | "extracted" | "extracted_confirmed";

export interface DocumentHistoryEntry {
  at: string;
  by: string | null;
  action: DocumentHistoryAction | string;
  status: DocumentStatus | null;
  note: string | null;
  version?: number | null;
}

/** Campos de `extracted.fields` que el usuario puede aplicar al documento (`PUT …/confirm-extracted { apply }`). */
export const APPLICABLE_EXTRACTED = ["reference", "issued_at", "expires_at"] as const;
export type ApplicableExtracted = (typeof APPLICABLE_EXTRACTED)[number];

/** Nivel de confianza de la lectura (#1631: `high | medium | low`). */
export type ExtractConfidence = "high" | "medium" | "low";

/** Un campo leído por la IA (#1631), aplanado para la tabla del panel. */
export interface ExtractedField {
  key: string;
  /** Valor legible (los objetos `shipper`/`consignee` se aplanan a «nombre · RFC · dirección»). */
  value: string | number | null;
  /** `true` si el API acepta aplicarlo al documento (`reference`, `issued_at`, `expires_at`). */
  applicable: boolean;
}

/**
 * Lectura de la versión vigente (IA o lector CFDI), tal como la guarda el API:
 * `fields` planos para la tabla, `confidence` global, tipo detectado/sugerido,
 * versión leída y confirmación. `applied` dice qué campos ya se pasaron al documento.
 */
export interface ExtractedData {
  fields: ExtractedField[];
  confidence: ExtractConfidence | null;
  detected_doc_type: string | null;
  suggested_doc_type: string | null;
  doc_type_matches: boolean | null;
  source: string | null;
  model: string | null;
  at: string | null;
  version: number | null;
  confirmed: boolean;
  confirmed_at: string | null;
  confirmed_by: string | null;
  applied: string[];
}

/** Motivos por los que una lectura no corrió o no se guardó (avisos, no errores). */
export type ExtractionReason = "queued" | "duplicate" | "disabled" | "no_file" | "already_extracted" | "sandbox" | "empty" | "too_large" | "xml_not_cfdi" | "unsupported_type" | "ai_not_configured" | "budget_exceeded" | "ai_error" | string;

export interface ExtractionOutcome {
  ran: boolean;
  reason: ExtractionReason | null;
}

/** Respuesta de `POST /documents` (201 nueva versión, 200 si el contenido ya era la versión vigente). */
export interface UploadResult {
  document: ShipmentDocument;
  duplicate: boolean;
  duplicate_reason: "same_content" | "same_origin" | string | null;
  extraction: ExtractionOutcome | null;
}

/** Respuesta de `POST /documents/:id/extract`. */
export interface ExtractResult {
  document: ShipmentDocument;
  extraction: ExtractionOutcome | null;
}

/** Respuesta de `POST /documents/subject/:kind/:id/generate` (201). */
export interface GenerateResult {
  document: ShipmentDocument;
  download: DownloadInfo | null;
}

export interface ShipmentDocument {
  id: string;
  company_id: string;
  subject: DocumentSubjectRef;
  subject_key: string;
  doc_type: string;
  category: DocumentCategory | null;
  name: { es: string; en: string };
  status: DocumentStatus;
  required: boolean;
  provided_by: DocumentProvider;
  client_visible: boolean;
  reference_only: boolean;
  reference: string | null;
  issued_at: string | null;
  expires_at: string | null;
  source: DocumentSource | null;
  current_version: number;
  versions: DocumentVersion[];
  extracted: ExtractedData | null;
  review: DocumentReview | null;
  history: DocumentHistoryEntry[];
  created_by?: string | null;
  created_at?: string | null;
  updated_by?: string | null;
  updated_at?: string | null;
  // Derivados por el servidor.
  effective_status: DocumentStatus;
  is_done: boolean;
  is_expired: boolean;
  expires_in_days: number | null;
  has_file: boolean;
}

export interface DocumentRequirement {
  doc_type: string;
  required: boolean;
  provided_by: DocumentProvider;
}

export interface DocumentSubject extends DocumentSubjectRef {
  company_id: string | null;
  mode: DocumentMode;
  direction: DocumentDirection;
  incoterm: string | null;
  label: string;
  reference: string | null;
  client_name: string | null;
  origin: string | null;
  destination: string | null;
  status_id?: string | null;
  booking_status?: string | null;
  created_at?: unknown;
  sandboxOf?: string | null;
}

export interface Completeness {
  total_required: number;
  done_required: number;
  percent: number;
  missing: string[];
  pending_review: string[];
  rejected: string[];
  expiring: string[];
  expired: string[];
}

export { SUBJECT_STATES, type SubjectState };

export interface DocumentChecklist extends Completeness {
  subject: DocumentSubject;
  materialized: boolean;
  requirements: DocumentRequirement[];
  documents: ShipmentDocument[];
  state: SubjectState;
  /** Sólo al materializar: tipos creados en esta llamada. */
  created?: string[];
}

export interface SubjectSummary extends DocumentSubject, Completeness {
  documents_count: number;
  state: SubjectState;
}

export interface DashboardIncompleteRow {
  subject: DocumentSubjectRef;
  percent: number;
  missing: string[];
  state: SubjectState;
}

export interface DashboardExpiringRow {
  id: string;
  subject: DocumentSubjectRef;
  doc_type: string;
  name: { es: string; en: string };
  expires_at: string;
  expires_in_days: number;
}

export interface DocumentsDashboard {
  company_id: string;
  window_days: number;
  documents_total: number;
  by_status: Record<DocumentStatus, number>;
  subjects_total: number;
  subjects_by_kind: Record<SubjectKind, number>;
  subjects_complete: number;
  subjects_incomplete: number;
  subjects_with_expired: number;
  pending_review: number;
  rejected: number;
  expiring: number;
  expired: number;
  average_completeness: number;
  top_incomplete: DashboardIncompleteRow[];
  expiring_soon: DashboardExpiringRow[];
  /** Documentos generados por la plataforma en el mes en curso (#1631). Si el API no lo manda, se calcula del repositorio. */
  generated_this_month?: number;
}

export type TemplateItem = { doc_type: string; required: boolean; provided_by: DocumentProvider } | { doc_type: string; remove: true };

export interface RequirementTemplate {
  mode: DocumentMode;
  direction: DocumentDirection;
  incoterm: string | null;
  items: TemplateItem[];
}

export interface DocumentRequirements {
  base: RequirementTemplate[];
  company: RequirementTemplate[];
}

export interface DocumentTypesCatalog {
  types: DocumentTypeSpec[];
  statuses: DocumentStatus[];
  sources: DocumentSource[];
  categories: DocumentCategory[];
  modes: DocumentMode[];
  directions: DocumentDirection[];
  providers: DocumentProvider[];
  incoterms: string[];
  subject_kinds: SubjectKind[];
  /** Tipos que nunca pueden marcarse visibles al cliente (carátula, borrador de Carta Porte). */
  never_client_visible: string[];
}

export interface DownloadInfo {
  url: string;
  expires_at: string;
  version: number;
  file_name: string | null;
  mime: string | null;
  size: number | null;
}

/** Un generador de documentos de la plataforma para un sujeto (`GET /documents/generators?kind=&id=`, #1631). */
export interface DocumentGenerator {
  id: string;
  doc_type: string;
  name: { es: string; en: string };
  description: { es: string; en: string } | null;
  modes: DocumentMode[] | null;
  /** `false` cuando no aplica al sujeto: por modo (`reason: "mode"`) o por datos faltantes (`reason: "missing_data"`, ver `missing`). */
  available: boolean;
  reason: "mode" | "missing_data" | string | null;
  /** Campos del embarque que faltan para generarlo. */
  missing: string[];
  /** Documento ya generado/subido de ese tipo, si lo hay. */
  existing: { id: string; status: DocumentStatus; current_version: number; source: DocumentSource | null; updated_at: string | null } | null;
}

export interface UploadInput {
  subject: DocumentSubjectRef;
  doc_type: string;
  file?: File | null;
  reference?: string | null;
  issued_at?: string | null;
  expires_at?: string | null;
  client_visible?: boolean;
  required?: boolean;
  provided_by?: DocumentProvider;
  note?: string | null;
}

export interface DocumentPatch {
  status?: Extract<DocumentStatus, "pendiente" | "recibido" | "en_revision" | "aprobado" | "rechazado">;
  note?: string | null;
  reference?: string | null;
  issued_at?: string | null;
  expires_at?: string | null;
  client_visible?: boolean;
  required?: boolean;
  provided_by?: DocumentProvider;
}

export interface SubjectsQuery {
  kind?: SubjectKind;
  status?: SubjectState;
  q?: string;
  limit?: number;
}

export interface ListQuery {
  type?: string;
  status?: DocumentStatus;
  from?: string;
  to?: string;
  q?: string;
  limit?: number;
}

// ---------------------------------------------------------------------------
// Utilidades
// ---------------------------------------------------------------------------

const unwrap = <T,>(res: { data: { data?: T } & Record<string, unknown> }): T => (res.data && "data" in res.data && res.data.data !== undefined ? (res.data.data as T) : (res.data as unknown as T));

const asArray = <T,>(v: unknown): T[] => (Array.isArray(v) ? (v as T[]) : []);
const asStr = (v: unknown): string | null => (typeof v === "string" ? v : typeof v === "number" ? String(v) : null);
const asNum = (v: unknown): number | null => (typeof v === "number" && Number.isFinite(v) ? v : null);
const asName = (v: unknown, fallback: string): { es: string; en: string } => {
  const o = (v && typeof v === "object" ? v : {}) as { es?: unknown; en?: unknown };
  const es = asStr(o.es) ?? (typeof v === "string" ? v : fallback);
  const en = asStr(o.en) ?? es;
  return { es, en };
};

/** `true` si el error es un 404 del API (función aún no desplegada: «próximamente»). */
export function isNotAvailable(err: unknown): boolean {
  return axios.isAxiosError(err) && err.response?.status === 404;
}

/** Mensaje legible de un error del API (meta.message / message) o `fallback`. */
export function documentsErrorMessage(err: unknown, fallback: string): string {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data as { meta?: { message?: string }; message?: string } | undefined;
    return data?.meta?.message || data?.message || err.message || fallback;
  }
  return err instanceof Error && err.message ? err.message : fallback;
}

/** Valor legible de un campo leído: escalares tal cual, partes como «nombre · RFC · dirección», listas de referencias como «tipo valor». */
function flattenExtractedValue(v: unknown): string | number | null {
  if (v === null || v === undefined || v === "") return null;
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  if (typeof v === "string") return v;
  if (typeof v === "boolean") return v ? "true" : "false";
  if (Array.isArray(v)) {
    const parts = v.map((x) => (x && typeof x === "object" ? [asStr((x as Record<string, unknown>).type), asStr((x as Record<string, unknown>).value)].filter(Boolean).join(" ") : asStr(x))).filter(Boolean);
    return parts.length ? parts.join(", ") : null;
  }
  if (typeof v === "object") {
    const o = v as Record<string, unknown>;
    const parts = [asStr(o.name), asStr(o.rfc), asStr(o.address)].filter(Boolean);
    return parts.length ? parts.join(" · ") : null;
  }
  return null;
}

/** Normaliza `extracted` (contrato de #1631: `fields` objeto plano + metadatos); nunca truena con una forma inesperada. */
export function normalizeExtracted(raw: unknown): ExtractedData | null {
  if (!raw || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  let fields: ExtractedField[] = [];
  if (Array.isArray(o.fields)) {
    fields = (o.fields as Record<string, unknown>[])
      .filter((f) => f && typeof f === "object")
      .map((f) => ({ key: asStr(f.key) ?? asStr(f.name) ?? "", value: flattenExtractedValue(f.value), applicable: false }));
  } else if (o.fields && typeof o.fields === "object") {
    fields = Object.entries(o.fields as Record<string, unknown>).map(([key, v]) => ({ key, value: flattenExtractedValue(v), applicable: false }));
  }
  fields = fields.filter((f) => f.key).map((f) => ({ ...f, applicable: (APPLICABLE_EXTRACTED as readonly string[]).includes(f.key) && f.value !== null }));
  const conf = asStr(o.confidence);
  return {
    fields,
    confidence: conf === "high" || conf === "medium" || conf === "low" ? conf : null,
    detected_doc_type: asStr(o.detected_doc_type),
    suggested_doc_type: asStr(o.suggested_doc_type),
    doc_type_matches: typeof o.doc_type_matches === "boolean" ? o.doc_type_matches : null,
    source: asStr(o.source),
    model: asStr(o.model),
    at: asStr(o.at) ?? asStr(o.extracted_at),
    version: asNum(o.version),
    confirmed: o.confirmed === true || Boolean(asStr(o.confirmed_at)),
    confirmed_at: asStr(o.confirmed_at),
    confirmed_by: asStr(o.confirmed_by),
    applied: asArray<string>(o.applied),
  };
}

function normalizeExtraction(raw: unknown): ExtractionOutcome | null {
  if (!raw || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  return { ran: o.ran === true, reason: asStr(o.reason) };
}

/** La respuesta de subir/leer trae el documento plano más `extraction`/`duplicate` (o `{ document }` si el API lo envuelve). */
function splitDocumentEnvelope(data: unknown): { doc: unknown; extra: Record<string, unknown> } {
  const o = (data && typeof data === "object" ? data : {}) as Record<string, unknown>;
  if (o.document && typeof o.document === "object") return { doc: o.document, extra: o };
  return { doc: o, extra: o };
}

/** Normaliza un documento del API (rellena lo que un backend anterior no mande). */
export function normalizeDocument(raw: unknown): ShipmentDocument {
  const d = (raw && typeof raw === "object" ? raw : {}) as Record<string, unknown>;
  const subject = (d.subject && typeof d.subject === "object" ? d.subject : { kind: "shipment", id: "" }) as DocumentSubjectRef;
  const status = (DOCUMENT_STATUSES as readonly string[]).includes(String(d.status)) ? (d.status as DocumentStatus) : "pendiente";
  const effective = (DOCUMENT_STATUSES as readonly string[]).includes(String(d.effective_status)) ? (d.effective_status as DocumentStatus) : status;
  const versions = asArray<DocumentVersion>(d.versions);
  return {
    id: String(d.id ?? ""),
    company_id: String(d.company_id ?? ""),
    subject,
    subject_key: asStr(d.subject_key) ?? `${subject.kind}:${subject.id}`,
    doc_type: String(d.doc_type ?? ""),
    category: (asStr(d.category) as DocumentCategory | null) ?? DOCUMENT_TYPES[String(d.doc_type ?? "")]?.category ?? null,
    // Sin `name` del API se usa el del catálogo (y el id si el tipo no existe).
    name: d.name && typeof d.name === "object" ? asName(d.name, String(d.doc_type ?? "")) : DOCUMENT_TYPES[String(d.doc_type ?? "")]?.name ?? { es: String(d.doc_type ?? ""), en: String(d.doc_type ?? "") },
    status,
    required: d.required !== false,
    provided_by: (asStr(d.provided_by) as DocumentProvider | null) ?? "cliente",
    client_visible: d.client_visible === true,
    reference_only: d.reference_only === true,
    reference: asStr(d.reference),
    issued_at: asStr(d.issued_at),
    expires_at: asStr(d.expires_at),
    source: (asStr(d.source) as DocumentSource | null) ?? null,
    current_version: asNum(d.current_version) ?? versions.length,
    versions,
    extracted: normalizeExtracted(d.extracted),
    review: (d.review && typeof d.review === "object" ? (d.review as DocumentReview) : null),
    history: asArray<DocumentHistoryEntry>(d.history),
    created_by: asStr(d.created_by),
    created_at: asStr(d.created_at),
    updated_by: asStr(d.updated_by),
    updated_at: asStr(d.updated_at),
    effective_status: effective,
    is_done: typeof d.is_done === "boolean" ? d.is_done : effective === "aprobado" || effective === "generado",
    is_expired: typeof d.is_expired === "boolean" ? d.is_expired : effective === "vencido",
    expires_in_days: asNum(d.expires_in_days),
    has_file: typeof d.has_file === "boolean" ? d.has_file : versions.length > 0,
  };
}

function normalizeChecklist(raw: unknown): DocumentChecklist {
  const c = (raw && typeof raw === "object" ? raw : {}) as Record<string, unknown>;
  const documents = asArray(c.documents).map(normalizeDocument);
  return {
    ...(c as unknown as DocumentChecklist),
    subject: c.subject as DocumentSubject,
    materialized: c.materialized === true,
    requirements: asArray<DocumentRequirement>(c.requirements),
    documents,
    state: (asStr(c.state) as SubjectState | null) ?? (documents.length ? "incompleto" : "sin_expediente"),
    total_required: asNum(c.total_required) ?? 0,
    done_required: asNum(c.done_required) ?? 0,
    percent: asNum(c.percent) ?? 0,
    missing: asArray<string>(c.missing),
    pending_review: asArray<string>(c.pending_review),
    rejected: asArray<string>(c.rejected),
    expiring: asArray<string>(c.expiring),
    expired: asArray<string>(c.expired),
    created: c.created === undefined ? undefined : asArray<string>(c.created),
  };
}

function normalizeGenerator(raw: unknown): DocumentGenerator | null {
  if (typeof raw === "string") return { id: raw, doc_type: raw, name: DOCUMENT_TYPES[raw]?.name ?? { es: raw, en: raw }, description: null, modes: null, available: true, reason: null, missing: [], existing: null };
  if (!raw || typeof raw !== "object") return null;
  const g = raw as Record<string, unknown>;
  const id = asStr(g.id) ?? asStr(g.doc_type);
  if (!id) return null;
  const docType = asStr(g.doc_type) ?? id;
  const existing = g.existing && typeof g.existing === "object" ? (g.existing as Record<string, unknown>) : null;
  return {
    id,
    doc_type: docType,
    name: g.name ? asName(g.name, docType) : DOCUMENT_TYPES[docType]?.name ?? { es: docType, en: docType },
    description: g.description ? asName(g.description, "") : null,
    modes: Array.isArray(g.modes) ? (g.modes as DocumentMode[]) : null,
    available: g.available !== false,
    reason: asStr(g.reason),
    missing: asArray<string>(g.missing),
    existing: existing
      ? { id: String(existing.id ?? ""), status: (asStr(existing.status) as DocumentStatus | null) ?? "generado", current_version: asNum(existing.current_version) ?? 0, source: (asStr(existing.source) as DocumentSource | null) ?? null, updated_at: asStr(existing.updated_at) }
      : null,
  };
}

/** Número de mes/año en curso de una fecha ISO (para «generados del mes»). */
export function isInCurrentMonth(iso: string | null | undefined, nowMs = Date.now()): boolean {
  if (!iso) return false;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return false;
  const now = new Date(nowMs);
  return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
}

function toFormData(input: UploadInput): FormData {
  const fd = new FormData();
  fd.append("subject_kind", input.subject.kind);
  fd.append("subject_id", input.subject.id);
  fd.append("doc_type", input.doc_type);
  if (input.file) fd.append("file", input.file, input.file.name);
  if (input.reference) fd.append("reference", input.reference);
  if (input.issued_at) fd.append("issued_at", input.issued_at);
  if (input.expires_at) fd.append("expires_at", input.expires_at);
  if (typeof input.client_visible === "boolean") fd.append("client_visible", String(input.client_visible));
  if (typeof input.required === "boolean") fd.append("required", String(input.required));
  if (input.provided_by) fd.append("provided_by", input.provided_by);
  if (input.note) fd.append("note", input.note);
  // `source` nunca se manda: todo lo que entra por aquí es `upload`; `generated`
  // y `mail_radar` los fija el servidor (#1631).
  return fd;
}

/** Identificador del error del API (`meta.messageKey` o `meta.code`; el `message` llega traducido y no sirve de señal). */
export function documentsErrorKey(err: unknown): string | null {
  if (!axios.isAxiosError(err)) return null;
  const meta = (err.response?.data as { meta?: { code?: string; messageKey?: string } } | undefined)?.meta;
  return meta?.messageKey || meta?.code || null;
}

/** `true` si el API rechazó por tope de versiones (422 `documentVersionsLimit`, 50 por documento). */
export function isVersionsLimitError(err: unknown): boolean {
  return axios.isAxiosError(err) && err.response?.status === 422 && documentsErrorKey(err) === "documentVersionsLimit";
}

/** `true` si el API rechazó las plantillas (422 `documentTemplatesInvalid`: duplicadas, tipo desconocido o sin ningún obligatorio). */
export function isTemplatesInvalidError(err: unknown): boolean {
  return axios.isAxiosError(err) && err.response?.status === 422 && (documentsErrorKey(err) === "documentTemplatesInvalid" || documentsErrorKey(err) === null);
}

/** 422 `documentExtractVersionStale`: se pidió leer una versión que ya no es la vigente. */
export function isExtractStaleError(err: unknown): boolean {
  return axios.isAxiosError(err) && err.response?.status === 422 && documentsErrorKey(err) === "documentExtractVersionStale";
}

/** 429 `tooManyRequests` (30 lecturas por compañía y hora). */
export function isTooManyRequestsError(err: unknown): boolean {
  return axios.isAxiosError(err) && err.response?.status === 429;
}

/** 422 `documentClientVisibleNotAllowed`: ese tipo nunca se muestra al cliente. */
export function isClientVisibleNotAllowedError(err: unknown): boolean {
  return axios.isAxiosError(err) && err.response?.status === 422 && documentsErrorKey(err) === "documentClientVisibleNotAllowed";
}

/** Campos faltantes de un 422 `documentGenerateMissingData` (`details.missing`); `null` si no es ese error. */
export function generateMissingFields(err: unknown): string[] | null {
  if (!axios.isAxiosError(err) || err.response?.status !== 422 || documentsErrorKey(err) !== "documentGenerateMissingData") return null;
  const meta = (err.response?.data as { meta?: { details?: { missing?: unknown }; missing?: unknown } } | undefined)?.meta;
  return asArray<string>(meta?.details?.missing ?? meta?.missing);
}

const withCompany = (companyId: string | null | undefined, params: object = {}) => {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(params as Record<string, unknown>)) if (v !== undefined && v !== null && v !== "") out[k] = v;
  if (companyId) out.companyId = companyId;
  return out;
};

// ---------------------------------------------------------------------------
// Servicio
// ---------------------------------------------------------------------------

export const shipmentDocumentsService = {
  /** Catálogo del API (tipos, estados, modos, incoterms…). */
  async types(): Promise<DocumentTypesCatalog> {
    const data = unwrap<Partial<DocumentTypesCatalog>>(await api.get("/documents/types"));
    return {
      types: asArray<DocumentTypeSpec>(data.types),
      statuses: asArray<DocumentStatus>(data.statuses),
      sources: asArray<DocumentSource>(data.sources),
      categories: asArray<DocumentCategory>(data.categories),
      modes: asArray<DocumentMode>(data.modes),
      directions: asArray<DocumentDirection>(data.directions),
      providers: asArray<DocumentProvider>(data.providers),
      incoterms: asArray<string>(data.incoterms).length ? asArray<string>(data.incoterms) : [...INCOTERMS],
      subject_kinds: asArray<SubjectKind>(data.subject_kinds),
      // El API lo marca por tipo (`never_client_visible: true`); un API anterior no lo trae y se usa el espejo.
      never_client_visible: Array.isArray((data as { never_client_visible?: unknown }).never_client_visible)
        ? asArray<string>((data as { never_client_visible?: unknown }).never_client_visible)
        : asArray<DocumentTypeSpec>(data.types).length
          ? asArray<DocumentTypeSpec>(data.types).filter((t) => t.never_client_visible === true).map((t) => t.id)
          : Object.values(DOCUMENT_TYPES).filter((t) => t.never_client_visible === true).map((t) => t.id),
    };
  },

  async dashboard(companyId?: string | null, days = 30): Promise<DocumentsDashboard> {
    const d = unwrap<DocumentsDashboard>(await api.get("/documents/dashboard", { params: withCompany(companyId, { days }) }));
    return { ...d, top_incomplete: asArray(d.top_incomplete), expiring_soon: asArray(d.expiring_soon), by_status: d.by_status ?? ({} as DocumentsDashboard["by_status"]) };
  },

  async subjects(companyId?: string | null, query: SubjectsQuery = {}): Promise<SubjectSummary[]> {
    return asArray<SubjectSummary>(unwrap(await api.get("/documents/subjects", { params: withCompany(companyId, query) })));
  },

  async expiring(companyId?: string | null, days = 30): Promise<ShipmentDocument[]> {
    return asArray(unwrap(await api.get("/documents/expiring", { params: withCompany(companyId, { days }) }))).map(normalizeDocument);
  },

  async list(companyId?: string | null, query: ListQuery = {}): Promise<ShipmentDocument[]> {
    return asArray(unwrap(await api.get("/documents/list", { params: withCompany(companyId, query) }))).map(normalizeDocument);
  },

  async requirements(companyId?: string | null): Promise<DocumentRequirements> {
    const r = unwrap<Partial<DocumentRequirements>>(await api.get("/documents/requirements", { params: withCompany(companyId) }));
    return { base: asArray<RequirementTemplate>(r.base), company: asArray<RequirementTemplate>(r.company) };
  },

  async saveRequirements(templates: RequirementTemplate[], companyId?: string | null): Promise<DocumentRequirements> {
    const r = unwrap<Partial<DocumentRequirements>>(await api.put("/documents/requirements", { templates, ...(companyId ? { companyId } : {}) }));
    return { base: asArray<RequirementTemplate>(r.base), company: asArray<RequirementTemplate>(r.company) };
  },

  async checklist(subject: DocumentSubjectRef, companyId?: string | null): Promise<DocumentChecklist> {
    return normalizeChecklist(unwrap(await api.get(`/documents/subject/${subject.kind}/${encodeURIComponent(subject.id)}`, { params: withCompany(companyId) })));
  },

  async materialize(subject: DocumentSubjectRef, companyId?: string | null): Promise<DocumentChecklist> {
    return normalizeChecklist(unwrap(await api.post(`/documents/subject/${subject.kind}/${encodeURIComponent(subject.id)}/materialize`, companyId ? { companyId } : {})));
  },

  /**
   * Sube un archivo (o registra un folio) con progreso 0–100. El API responde de
   * inmediato: 201 con `extraction.reason: "queued"` (la IA lee en segundo plano
   * ≤ 60 s; relee el documento con `get`/`pollExtraction`) o 200 con
   * `duplicate: true` si el contenido ya era la versión vigente.
   */
  async upload(input: UploadInput, onProgress?: (pct: number) => void): Promise<UploadResult> {
    const res = await apiFormData.post("/documents", toFormData(input), {
      onUploadProgress: (e: AxiosProgressEvent) => {
        if (!onProgress) return;
        const total = e.total ?? (input.file ? input.file.size : 0);
        onProgress(total ? Math.min(100, Math.round((e.loaded / total) * 100)) : 100);
      },
    });
    const { doc, extra } = splitDocumentEnvelope(unwrap(res));
    return { document: normalizeDocument(doc), duplicate: extra.duplicate === true, duplicate_reason: asStr(extra.duplicate_reason), extraction: normalizeExtraction(extra.extraction) };
  },

  /**
   * Relee el documento hasta que `extracted` tenga la versión vigente (la
   * lectura encolada al subir). `times` intentos cada `intervalMs`; devuelve el
   * último documento leído (con o sin lectura) y llama `onTick` en cada intento.
   */
  async pollExtraction(id: string, { times = 4, intervalMs = 5000, onTick, sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms)) }: { times?: number; intervalMs?: number; onTick?: (doc: ShipmentDocument, attempt: number) => void; sleep?: (ms: number) => Promise<void> } = {}): Promise<ShipmentDocument> {
    let last: ShipmentDocument | null = null;
    for (let attempt = 1; attempt <= times; attempt++) {
      await sleep(intervalMs);
      last = await this.get(id);
      onTick?.(last, attempt);
      if (last.extracted && (last.extracted.version === null || last.extracted.version === last.current_version)) return last;
    }
    return last ?? (await this.get(id));
  },

  async update(id: string, patch: DocumentPatch): Promise<ShipmentDocument> {
    return normalizeDocument(unwrap(await api.put(`/documents/${encodeURIComponent(id)}`, patch)));
  },

  async get(id: string): Promise<ShipmentDocument> {
    return normalizeDocument(unwrap(await api.get(`/documents/${encodeURIComponent(id)}`)));
  },

  async downloadUrl(id: string, version?: number): Promise<DownloadInfo> {
    return unwrap<DownloadInfo>(await api.get(`/documents/${encodeURIComponent(id)}/download`, { params: version ? { version } : {} }));
  },

  // ---- Generación y lectura con IA (#1631) ---------------------------------

  /**
   * Generadores para un sujeto (`GET /documents/generators?kind=&id=`): cada uno
   * con `available`, `missing` y `reason`. `null` si el backend aún no expone la
   * función (404 de la ruta; un sujeto inexistente también responde 404 y se trata igual).
   */
  async generators(subject: DocumentSubjectRef, companyId?: string | null): Promise<DocumentGenerator[] | null> {
    try {
      const data = unwrap<unknown>(await api.get("/documents/generators", { params: withCompany(companyId, { kind: subject.kind, id: subject.id }) }));
      const list = Array.isArray(data) ? data : asArray((data as { generators?: unknown })?.generators);
      return list.map(normalizeGenerator).filter((g): g is DocumentGenerator => g !== null);
    } catch (err) {
      if (isNotAvailable(err)) return null;
      throw err;
    }
  },

  /**
   * Genera y guarda un documento del sujeto (`doc_type`: packing_list, sli,
   * caratula, carta_porte_borrador, pod; `options` completa datos). 201 con el
   * documento y su `download`; 422 `documentGenerateMissingData` trae `details.missing`.
   */
  async generate(subject: DocumentSubjectRef, docType: string, options: Record<string, unknown> = {}): Promise<GenerateResult> {
    const res = await api.post(`/documents/subject/${subject.kind}/${encodeURIComponent(subject.id)}/generate`, { doc_type: docType, options });
    const { doc, extra } = splitDocumentEnvelope(unwrap(res));
    const download = extra.download && typeof extra.download === "object" ? (extra.download as DownloadInfo) : null;
    return { document: normalizeDocument(doc), download: download && typeof download.url === "string" ? download : null };
  },

  /**
   * Lee con IA la versión vigente (`POST /documents/:id/extract[?force=true]`).
   * Si ya había lectura responde `extraction.reason: already_extracted` (salvo
   * `force`); otros motivos (`budget_exceeded`, `ai_not_configured`, `sandbox`,
   * `too_large`…) también llegan en `extraction.reason`, no como error.
   */
  async extract(id: string, { force = false }: { force?: boolean } = {}): Promise<ExtractResult> {
    const { doc, extra } = splitDocumentEnvelope(unwrap(await api.post(`/documents/${encodeURIComponent(id)}/extract`, {}, { params: force ? { force: true } : {} })));
    return { document: normalizeDocument(doc), extraction: normalizeExtraction(extra.extraction) };
  },

  /** Confirma la lectura: `apply` dice qué campos pasan al documento (`reference`, `issued_at`, `expires_at`; vacío = confirmar sin aplicar). */
  async confirmExtracted(id: string, apply: ApplicableExtracted[]): Promise<ShipmentDocument> {
    const { doc } = splitDocumentEnvelope(unwrap(await api.put(`/documents/${encodeURIComponent(id)}/confirm-extracted`, { apply })));
    return normalizeDocument(doc);
  },
};

export type ShipmentDocumentsService = typeof shipmentDocumentsService;
